// src/prototype/components/EAJourneyBarFrame.tsx
// ============================================================
// JOURNEY BAR FRAME — the ONLY place the spacing around the journey bar is set.
//
// Every surface that renders EAJourneyBar renders it through this frame. Pages must not
// apply their own pt-/pb-/px- to a journey-bar container: that is exactly how the spacing
// drifted in the first place (pt-2, pt-3, pt-4, pt-5 and one page with no wrapper at all,
// across fourteen call sites).
//
// ── VERTICAL RHYTHM ─────────────────────────────────────────────────────────────────────
// One spacing step (JOURNEY_BAR_STEP = 1.25rem) is used everywhere, but WHERE the bottom
// step lands depends on whether the frame carries a tab row.
//
//   No tab row (most surfaces)        With a tab row (Evaluate)
//   ─────────────────────────         ─────────────────────────
//     [ step ]                          [ step ]
//     journey bar                       journey bar
//     [ step ]                          [ step ]        <- same breathing space...
//     page content                      tab row
//                                       (flush)         <- ...but the frame ends here
//                                       page content
//
// With a tab row the bottom step moves ABOVE the row and the frame's own bottom padding
// goes to zero, so the tabs sit directly on the content they control rather than floating
// between the bar and the page. Detached tabs read as page furniture; flush tabs read as
// tabs. The page below must not reintroduce a top gap — see `contentIsFlush` on the pages
// that use the slot.
//
// What legitimately varies per page is preserved and stays opt-in:
//   sticky   — whether the frame pins to the top on scroll (unchanged per page)
//   bordered — the hairline + shadow the Evaluate page uses to separate its sticky header
//   children — the tab row, rendered in the slot described above
// ============================================================

import React from 'react';
import { EAJourneyBar } from './EAJourneyBar';

/**
 * The single spacing step used above the bar, and below it (or above the tab row when one
 * is present). Tailwind step 5 = 1.25rem. These must stay literal class strings so the
 * Tailwind scanner can see them.
 */
export const JOURNEY_BAR_PAD_TOP = 'pt-5';
export const JOURNEY_BAR_PAD_BOTTOM = 'pb-5';
/** Gap between the journey bar and a tab row — the same step as the frame's padding. */
export const JOURNEY_BAR_TAB_GAP = 'pt-5';

type EAJourneyBarProps = React.ComponentProps<typeof EAJourneyBar>;

export interface EAJourneyBarFrameProps extends EAJourneyBarProps {
  /** Pin the frame to the top on scroll. Per-page, unchanged by this component. */
  sticky?: boolean;
  /** Hairline + shadow under the frame (the Evaluate page's sticky header treatment). */
  bordered?: boolean;
  /**
   * Tab row rendered in the frame's tab slot: one spacing step above it, ZERO below, so it
   * sits flush on the content section. Supplying this changes where the bottom step lands.
   */
  children?: React.ReactNode;
}

export function EAJourneyBarFrame({
  sticky = false,
  bordered = false,
  compact = false,
  children,
  ...barProps
}: EAJourneyBarFrameProps) {
  const hasTabRow = Boolean(children);

  const classes = [
    sticky ? 'sticky top-0 z-10' : '',
    'bg-[#F5F7FA]',
    compact ? 'pt-3' : JOURNEY_BAR_PAD_TOP,
    // With a tab row the bottom step moves above the row (below), leaving the frame flush.
    hasTabRow ? 'pb-0' : compact ? 'pb-3' : JOURNEY_BAR_PAD_BOTTOM,
    'px-6',
    bordered ? 'border-b border-[#e5e7eb]/60 shadow-sm' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      <EAJourneyBar compact={compact} {...barProps} />
      {hasTabRow && <div className={JOURNEY_BAR_TAB_GAP}>{children}</div>}
    </div>
  );
}

export default EAJourneyBarFrame;
